import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { currentBaseUrl } from '../helpers/urlConst';

import { EVENT, trigger } from '../commons/utils/custom-event';
import {
  selectFullName,
  selectAddress,
  selectAdditionalData,
  selectMethods,
  selectStatus,
  selectShipping,
} from '../modules/shipping/slice';
import { setStatus, selectBilling, setFullName, setAddressGoogle } from '../modules/billing/slice';
import { identifyUserNameEmail, smartLookEvent } from '../helpers/analyticsHelper';
import { convertAddressToBackend } from '../modules/shipping/utils';
import { currentPaymentMethod } from '../modules/payment/slice';
import { usePickup } from '../modules/shipping/containers/pickup-in-store/use-pickup';

import { convertGiftsToBackend } from '../components/Coupon/CouponUtils';
import useApiStatus from '../store/apiStatus/useApiStatus';
import useErrors from '../store/errors/useErrors';
import DataContext from '../store/dataContext';
import UiContext from '../store/uiContext';
import { defaultUrl } from '../commons/hooks/use-platform';
import { useStoreApi } from './useStoreApi';
import { paymentIdList } from '../helpers/constants';
import { useProduct } from './useProduct';
import useCartTotalsUtils from './useCartTotalsUtils';
import useBillingPhone from '../commons/hooks/use-billing-phone';

import { useBroadcastChannel } from './useBroadcastChannel';
import useApiPostOrderError from './useApiPostOrderError';
import { useSubmitOrderApi } from './useAxios';
import useTagManager from './useTagManager';

const useSubmit = () => {
  const { getApiPostOrderErrorCode, hasBillingError } = useApiPostOrderError();
  const { addErrorToField } = useErrors();
  const { apiStatus } = useApiStatus();
  const { receiveMessage } = useBroadcastChannel();
  const dataContext = useContext(DataContext);
  const { dataLayer } = useTagManager();
  const uiContext = useContext(UiContext);
  const { allDigital } = useProduct();
  const { storeVar } = useStoreApi();
  const { t } = useTranslation();
  const {
    apiResponse: apiPostOrderResponse,
    apiError: apiPostOrderError,
    apiLoading: apiPostOrderLoading,
    apiPostOrder,
  } = useSubmitOrderApi();
  const { isBillingPhoneOk } = useBillingPhone();

  const shippingFullName = useSelector(selectFullName);
  const shippingAddress = useSelector(selectAddress);
  const shippingAddressPayload = convertAddressToBackend(shippingAddress, shippingFullName.value);
  const shippingAdditionalData = useSelector(selectAdditionalData);
  const shippingMethods = useSelector(selectMethods);
  const shippingStatus = useSelector(selectStatus);
  const shippingState = useSelector(selectShipping);

  const billingSelector = useSelector(selectBilling);
  const selectedMethod = useSelector(currentPaymentMethod);
  const giftCertificates = convertGiftsToBackend(dataContext.giftCertificates);
  const dispatch = useDispatch();

  const {
    isCheckoutReady,
    contextBillingAddress,
    isThirdPartyReady,
    handleOrderSubmit,
    storeUrl,
    setShouldRedirectOnError,
    moveToPlatform,
    applyStoreCredit,
  } = dataContext;

  const creditCardType = selectedMethod === paymentIdList.CREDIT_CARD;

  const { getOrderTotal } = useCartTotalsUtils();

  const { handlePickupSubmit, isPickupActive } = usePickup();

  const handlePaymentAdd = type => {
    let billingAddressPayload = {};

    if (isPickupActive()) {
      billingAddressPayload = {
        ...billingSelector.address,
        fullName: shippingState.fullName.value,
        line1: billingSelector.address.street,
        line2: billingSelector.address.apartmentUnit,
        postal: billingSelector.address.zip,
        region: billingSelector.address.state,
        regionDescription: billingSelector.address.stateLong,
      };
    } else if (dataContext.contextBillingAddress && Object.keys(dataContext.contextBillingAddress).length > 0) {
      billingAddressPayload = dataContext.contextBillingAddress;
    } else if (billingSelector.address.street) {
      billingAddressPayload = {
        ...billingSelector.address,
        fullName: billingSelector.fullName.value,
        line1: billingSelector.address.street,
        line2: billingSelector.address.apartmentUnit,
        postal: billingSelector.address.zip,
        region: billingSelector.address.state,
        regionDescription: billingSelector.address.stateLong,
      };
    }

    identifyUserNameEmail({
      userId: dataContext.email,
      shippingName: dataContext.shippingName,
    });

    const apiPostOrderPayload = {
      cartId: dataContext.cartId,
      deviceId: dataContext.device?.id,
      email: dataContext.email,
      userCardId: getOrderTotal() === 0 ? '0000' : dataContext.userCardId,
      userCardCvv: getOrderTotal() === 0 ? '111' : dataContext.userCardCvv,
      cardType: getOrderTotal() === 0 ? 'alt' : dataContext.cardType,
      contextBillingAddress: billingAddressPayload,
      billingPhone: dataContext.billingPhone,
      cvvChanged: dataContext.cvvChanged,
      isUserSaveInfo: dataContext.isUserSaveInfo,
      newsletter: dataContext.portalSettings.newsletter,
      orderNotes: dataContext.orderNotes,
      consent: dataContext.consent,
      giftCertificates,
      applyStoreCredit,
      type,
      socialImpact: {
        nonprofitId: storeVar?.nonprofitId,
        selectionId: storeVar?.selectionId,
      },
    };

    if (isPickupActive()) {
      apiPostOrderPayload.pickUpMethodKey = shippingState.bopis.selectedPickupMethod.lookupKey;
    } else {
      apiPostOrderPayload.shippingName = shippingState.fullName.value;
      apiPostOrderPayload.contextShippingAddress = shippingAddressPayload;
      apiPostOrderPayload.shipCompany = shippingAdditionalData.companyName;
      apiPostOrderPayload.shippingMethodId = shippingMethods.selectedKey;
    }

    apiPostOrder(apiPostOrderPayload);
  };

  const handleVenmoCheckout = () => {
    const div = document.getElementById('venmo-button-container');
    div.click();
  };

  const handleAmazonCheckout = () => {
    const popupRef = window.open(
      `${window.location.href}&amazon=openPopup`,
      'PromoteFirefoxWindowName',
      `popup, resizable=yes, height=678, width=487, top=73, left=524`,
    );
    uiContext.setShowOverlay({
      logo: null,
      titleLogo: `${currentBaseUrl()}assets/amazonpay-text.svg`,
      text: t('payment_method.amazonpay.overlay'),
      button: 'Continue',
      window: popupRef,
    });
    const timer = setInterval(() => {
      if (popupRef.closed) {
        clearInterval(timer);
        uiContext.setShowOverlay(null);
      }
    }, 1000);
    receiveMessage(popupRef);
  };

  const handleGoogleCheckout = () => {
    const div = document.getElementsByClassName('gpay-card-info-container black long en')[0];
    div.click();
  };

  const handlePaypalCheckout = () => {
    const popupRef = window.open(
      dataContext.paymentMethodData.paypal.href,
      'PromoteFirefoxWindowName',
      `popup, resizable=yes, height=678, width=487, top=73, left=524`,
    );
    uiContext.setShowOverlay({
      logo: `${currentBaseUrl()}assets/paypal-logo.svg`,
      titleLogo: `${currentBaseUrl()}assets/paypal-text.svg`,
      text: t('payment_method.paypal.overlay'),
      button: 'Continue',
      window: popupRef,
    });
    const timer = setInterval(() => {
      if (popupRef.closed) {
        clearInterval(timer);
        uiContext.setShowOverlay(null);
      }
    }, 1000);
    receiveMessage(popupRef);
  };

  const scrollToError = () => {
    document.getElementById('checkout.billing_information')?.scrollIntoView({ behavior: 'smooth' });
  };

  const isBillingNameOk = () => {
    if (hasBillingError() && !billingSelector.fullName.value && creditCardType) {
      dispatch(
        setFullName({
          error: {
            hasError: true,
            message: t('payment_add.please_enter_billing_name'),
          },
          value: billingSelector.fullName.value,
        }),
      );
      return false;
    }
    return true;
  };

  const isBillingAddressOk = () => {
    const hasContextBillingAddress = contextBillingAddress && Object.values(contextBillingAddress).length > 0;
    if (hasBillingError() && !hasContextBillingAddress && creditCardType) {
      dispatch(
        setAddressGoogle({
          addressGoogle: {
            error: {
              hasError: true,
              message: t('payment_add.please_enter_billing_address'),
            },
            value: billingSelector.addressGoogle.value,
          },
        }),
      );
      return false;
    }
    return true;
  };

  const checkBillingInformation = () => {
    const isBnOk = isBillingNameOk();
    const isBaOk = isBillingAddressOk();
    const isBpOk = isBillingPhoneOk();
    return isBnOk && isBaOk && isBpOk;
  };

  const handleClick = type => {
    const SubmitBtnType = type === 'submit';
    const PayPalType = type === 'paypal';
    const VenmoType = type === 'venmo';
    const AmazonPayType = type === paymentIdList.AMAZONPAY;
    const GooglePayType = type === paymentIdList.GOOGLEPAY;

    if (SubmitBtnType) {
      // Preventing the ability to trigger the Submit Event
      // for different payment types except for the credit card.
      // According to the task CHKOUT-1687
      trigger(EVENT.SUBMIT_CHECKOUT);
    }

    if (getApiPostOrderErrorCode() === 'NF_CHECKOUT_0008') {
      if (isBillingPhoneOk() && SubmitBtnType) {
        handlePaymentAdd(type);
      }
      if (checkBillingInformation() && VenmoType) {
        handleVenmoCheckout();
      }
      if (isBillingPhoneOk() && PayPalType) {
        if (moveToPlatform?.paypal) {
          window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
        } else {
          handlePaypalCheckout();
        }
      } else {
        scrollToError();
      }
    } else if (getApiPostOrderErrorCode() === 'NF_CHECKOUT_0009') {
      if (checkBillingInformation() && SubmitBtnType) {
        handlePaymentAdd(type);
      }
      if (checkBillingInformation() && VenmoType) {
        handleVenmoCheckout();
      }
      if (checkBillingInformation() && PayPalType) {
        if (moveToPlatform?.paypal) {
          window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
        } else {
          handlePaypalCheckout();
        }
      } else {
        scrollToError();
      }
    } else if (getApiPostOrderErrorCode() === 'NF_CHECKOUT_0110') {
      if (checkBillingInformation() && SubmitBtnType) {
        handlePaymentAdd(type);
      }
      if (checkBillingInformation() && VenmoType) {
        handleVenmoCheckout();
      }
      if (checkBillingInformation() && PayPalType) {
        if (moveToPlatform?.paypal) {
          window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
        } else {
          handlePaypalCheckout();
        }
      } else {
        scrollToError();
      }
    } else if (getApiPostOrderErrorCode() === 'NF_CHECKOUT_0111') {
      if (checkBillingInformation() && SubmitBtnType) {
        handlePaymentAdd(type);
      }
      if (checkBillingInformation() && AmazonPayType) {
        if (moveToPlatform?.amazonpay) {
          window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
        } else {
          handleAmazonCheckout();
        }
      } else {
        scrollToError();
      }
    } else {
      const isBpOk = isBillingPhoneOk();
      if (
        !isPickupActive() &&
        (apiStatus.isCardReady || (applyStoreCredit && getOrderTotal() === 0)) &&
        isBpOk &&
        SubmitBtnType &&
        isCheckoutReady({
          isShippingReady: shippingStatus.isReady || allDigital(),
          isBillingReady: billingSelector.status.isReady,
          zeroTotal: getOrderTotal() === 0,
          isAdditionalServiceLoading: uiContext.isLoadingProtection || uiContext.isLoadingSignature,
        })
      ) {
        handlePaymentAdd(type);
      }
      if (
        isThirdPartyReady({
          isShippingReady: shippingStatus.isReady || allDigital(),
          isBillingReady: billingSelector.status.isReady,
          isAdditionalServiceLoading: uiContext.isLoadingProtection || uiContext.isLoadingSignature,
        }) &&
        isBpOk &&
        PayPalType
      ) {
        if (moveToPlatform?.paypal) {
          window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
        } else {
          handlePaypalCheckout();
        }
      }
      if (
        isThirdPartyReady({
          isShippingReady: shippingStatus.isReady || allDigital(),
          isBillingReady: billingSelector.status.isReady,
          isAdditionalServiceLoading: uiContext.isLoadingProtection || uiContext.isLoadingSignature,
        }) &&
        isBpOk &&
        VenmoType
      ) {
        handleVenmoCheckout();
      }
      if (
        isThirdPartyReady({
          isShippingReady: shippingStatus.isReady || allDigital(),
          isBillingReady: billingSelector.status.isReady,
          isAdditionalServiceLoading: uiContext.isLoadingProtection || uiContext.isLoadingSignature,
        }) &&
        isBpOk &&
        AmazonPayType
      ) {
        if (moveToPlatform?.amazonpay) {
          window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
        } else {
          handleAmazonCheckout();
        }
      }
      if (
        isThirdPartyReady({
          isShippingReady: shippingStatus.isReady || allDigital(),
          isBillingReady: billingSelector.status.isReady,
          isAdditionalServiceLoading: uiContext.isLoadingProtection || uiContext.isLoadingSignature,
        }) &&
        isBpOk &&
        GooglePayType
      ) {
        if (moveToPlatform?.googlepay) {
          window.top.location.href = `${storeUrl}/${defaultUrl}?preventIframeLoad=true`;
        } else {
          handleGoogleCheckout();
        }
      }
    }

    handlePickupSubmit({
      creditCard: {
        isActive: type === 'submit',
        submitCallback: () => handlePaymentAdd(type),
      },
      googlePay: {
        isActive: type === paymentIdList.GOOGLEPAY,
        submitCallback: () => handleGoogleCheckout(),
      },
    });
  };

  const highlightBillingFields = () => {
    const hasContextBillingAddress =
      dataContext.contextBillingAddress && Object.values(dataContext.contextBillingAddress).length > 0;
    uiContext.updateCheckoutState(uiContext.TO_BILLING_ADDRESS_ADD);
    addErrorToField({
      billingName: {
        hasHighlight: !dataContext.billingName,
      },
      billingAddress: {
        hasHighlight: !hasContextBillingAddress,
      },
      billingPhone: {
        hasHighlight: !dataContext.billingPhone,
      },
    });
  };

  const isCardTypeNotAccepted = postOrderError => {
    const c1 = postOrderError?.errorCode?.id === 'NF4000';
    const c2 = postOrderError?.errorMsg === 'The merchant does not accept the provided payment method';
    if (c1 && c2) {
      return true;
    }
    return false;
  };

  const handleApiPostOrderError = postOrderError => {
    const error = postOrderError?.errorCode;
    if (error?.id === 'NF_CHECKOUT_0008') {
      dispatch(setStatus({ isReady: false }));
      uiContext.updateCardDeclined(true, false);
      addErrorToField({
        billingPhone: {
          hasHighlight: !dataContext.billingPhone,
        },
      });
    } else if (error?.id === 'NF_CHECKOUT_0009') {
      dispatch(setStatus({ isReady: false }));
      highlightBillingFields();
      uiContext.updateCardDeclined(false, true);
      smartLookEvent('requested-billing-info');
    } else if (error?.id === 'NF_CHECKOUT_0110') {
      dispatch(setStatus({ isReady: false }));
      highlightBillingFields();
      uiContext.updateCardDeclined(true, false);
      // we still track if CVV was changed (setCvvChanged)
      // but we should not prevent the user from submitting the order even if he decides not to change his CVV
      dataContext.setCvvChanged(false);
      smartLookEvent('requested-billing-info');
    } else if (error?.id === 'NF_CHECKOUT_0042' || error?.id === 'NF_CHECKOUT_0020') {
      uiContext.updateCardDeclined(true, false);
    } else if (!isCardTypeNotAccepted(postOrderError)) {
      setShouldRedirectOnError(true);
    }
  };

  useEffect(() => {
    if (apiPostOrderLoading) {
      dataContext.orderSubmitting(apiPostOrderLoading);
    }
  }, [apiPostOrderLoading]);

  useEffect(() => {
    if (apiPostOrderResponse?.success) {
      dataLayer(apiPostOrderResponse, 'Credit card');
      handleOrderSubmit(apiPostOrderResponse);
      uiContext.updateCheckoutState(uiContext.TO_DONE);
      if (!apiPostOrderResponse?.redirectUrl) {
        dataContext.orderSubmitting(false);
      }
      smartLookEvent('successful-order', { domain: window.location.hostname });
    }
  }, [apiPostOrderResponse]);

  useEffect(() => {
    if (apiPostOrderError?.data) {
      dataContext.setSubmitFailedTimestamp(new Date().getTime());
      dataContext.setApiPostOrderError(apiPostOrderError?.data);
      handleApiPostOrderError(apiPostOrderError?.data);
      dataContext.orderSubmitting(false);
    }
  }, [apiPostOrderError]);

  return { handlePaymentAdd, handleClick, checkBillingInformation };
};

export default useSubmit;
